import { Flex, Button, useDisclosure } from '@chakra-ui/react';
import { ReactComponent as SaveIcon } from '../../icons/save.svg';
import ConfirmationModal from 'components/modal/ConfirmationModal';
import { FormattedMessage } from 'react-intl';
import { WizardStep } from './types';

interface WizardNavigationProps {
    handleReset: () => void;
    isSubmitting: boolean;
    currentStep: WizardStep;
    canGoBack: boolean;
    handleBack: () => void;
    isLastStep: boolean;
}

/**
 * Renders navigation controls for a multi-step wizard, including Cancel, Back, and Next/Create buttons.
 * Displays confirmation modals for cancel and back actions when required.
 *
 * @param handleReset - Callback to reset the wizard state.
 * @param isSubmitting - Indicates if a submission is in progress, disabling actions.
 * @param currentStep - The current wizard step, including metadata for confirmation.
 * @param canGoBack - Whether the user can navigate to the previous step.
 * @param handleBack - Callback to navigate to the previous step.
 * @param isLastStep - Indicates if the current step is the final step in the wizard.
 *
 * @returns Wizard navigation UI with confirmation modals for sensitive actions.
 */
export function WizardNavigation({
    canGoBack,
    handleReset,
    isSubmitting,
    currentStep,
    handleBack,
    isLastStep,
}: WizardNavigationProps) {
    const cancelConfirmModal = useDisclosure();
    const backConfirmModal = useDisclosure();

    return (
        <>
            <Flex
                w="full"
                mt="auto"
                px="24px"
                py="10px"
                gap="16px"
                width="full"
                borderTop="1px"
                bg="background-secondary"
                color="gray.300"
                position="sticky"
                alignSelf="flex-end"
                justifyContent="flex-end"
                left={0}
                right={0}
                bottom={0}
                flexShrink={0}
            >
                <Button
                    textTransform={'capitalize'}
                    variant="secondary"
                    isDisabled={isSubmitting}
                    onClick={cancelConfirmModal.onOpen}
                >
                    <FormattedMessage id="common_cancel" />
                </Button>
                {canGoBack && (
                    <Button
                        variant="secondary"
                        textTransform={'capitalize'}
                        isDisabled={isSubmitting}
                        onClick={currentStep.confirmOnBack ? backConfirmModal.onOpen : handleBack}
                    >
                        <FormattedMessage id="common_back" />
                    </Button>
                )}
                <Button
                    textTransform={'capitalize'}
                    key={`${currentStep?.id}-${isSubmitting}`}
                    leftIcon={isLastStep ? <SaveIcon /> : undefined}
                    form={`form-${currentStep?.id}`}
                    type={'submit'}
                    isLoading={isSubmitting}
                    isDisabled={isSubmitting}
                >
                    {isLastStep ? (
                        <Flex>
                            <FormattedMessage id="common_create" />
                        </Flex>
                    ) : (
                        <FormattedMessage id="common_next_step" />
                    )}
                </Button>
            </Flex>
            {cancelConfirmModal.isOpen && (
                <ConfirmationModal
                    isOpen={true}
                    header={<FormattedMessage id="wizard_pcw_confirm" />}
                    content={<FormattedMessage id="wizard_pcw_cancel_confirmation" />}
                    confirmText={
                        <span style={{ textTransform: 'capitalize' }}>
                            <FormattedMessage id="common_yes" />
                        </span>
                    }
                    cancelText={
                        <span style={{ textTransform: 'capitalize' }}>
                            <FormattedMessage id="common_no" />
                        </span>
                    }
                    onConfirm={() => {
                        handleReset();
                        cancelConfirmModal.onClose();
                    }}
                    onClose={cancelConfirmModal.onClose}
                    hasExtraStep={false}
                    onExtraOptionClick={undefined}
                    extraOptionText={undefined}
                    additionalContent={undefined}
                />
            )}
            {backConfirmModal.isOpen && (
                <ConfirmationModal
                    isOpen={true}
                    header={<FormattedMessage id="wizard_pcw_confirm" />}
                    content={<FormattedMessage id="wizard_pcw_back_confirmation" />}
                    confirmText={
                        <span style={{ textTransform: 'capitalize' }}>
                            <FormattedMessage id="common_yes" />
                        </span>
                    }
                    cancelText={
                        <span style={{ textTransform: 'capitalize' }}>
                            <FormattedMessage id="common_no" />
                        </span>
                    }
                    onConfirm={() => {
                        handleBack();
                        backConfirmModal.onClose();
                    }}
                    onClose={backConfirmModal.onClose}
                    hasExtraStep={false}
                    onExtraOptionClick={undefined}
                    extraOptionText={undefined}
                    additionalContent={undefined}
                />
            )}
        </>
    );
}
